import { useRecords } from "../../api/useRecords";
import { useStore } from "../../store/store";
import {
    Badge,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Snackbar as MuiSnackBar,
    Typography,
} from "@mui/material";
import { CancelOutlined, MarkEmailRead, Notifications } from "@mui/icons-material";
import { Avatar } from "../fieldsViews/fields/Avatar";
import { UserFromAPI } from "../../types/auth.types";
import { formatDistanceToNow, parseISO } from "date-fns";
import pt from "date-fns/locale/pt";
import { removeHtmlTagsAndEntities } from "../../utils/removeHtmlTagsAndEntities";
import { getNotificationTypeLabel } from "../../utils/getNotificationTypeLabel";
import { useUpdateRecord } from "../../api/useUpdateRecord";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { MarkAllNotificationsRead } from "./MarkAllNotificationsRead";

const filterNotifications = {
    _and: [
        { _or: [{ is_read: { _eq: false } }, { is_read: { _null: true } }] },
        { user_to_be_notified: { _eq: "$CURRENT_USER" } },
    ],
};

const badgeFieldsQuery = "id,is_read,item.item.id";

const relatedItemCollectionsWithDateOnly = ["leads", "candidates", "clients", "treatments", "neurofeedback_data"];

const panelFieldsQuery = [
    "id",
    "date_created",
    "is_read",
    "content",
    "navigation_path",
    "type",
    "user_that_generated_notification.first_name",
    "user_that_generated_notification.last_name",
    "user_that_generated_notification.avatar",
    "item.item:tasks.id",
    "item.item:tasks.date_created",
    "item.item:tasks.status",
    "item.item:task_comments.id",
    "item.item:task_comments.date_created",
    "item.item:task_comments.task.status",
    ...relatedItemCollectionsWithDateOnly.flatMap((collection) => [
        `item.item:${collection}.id`,
        `item.item:${collection}.date_created`,
    ]),
].join(",");

export const NotificationsList = () => {
    const { notifications } = useStore();
    const isPanelOpen = notifications.notificationsList.isOpen;

    const { data: badgeData } = useRecords("notifications", badgeFieldsQuery, {
        extraOptions: { filter: filterNotifications },
        queryKeyExtraValue: "badge",
        fetch: {
            fetchInterval: 10 * 60 * 1000,
            fetchOnFocus: false,
            refetchIntervalInBackground: true,
        },
    });

    const { data } = useRecords("notifications", panelFieldsQuery, {
        extraOptions: { filter: filterNotifications },
        queryKeyExtraValue: "panel",
        enabled: isPanelOpen,
    });

    const [notificationsNumber, setNotificationsNumber] = useState<number | null>();
    const [fadingIds, setFadingIds] = useState<Set<string>>(new Set());
    const [pendingReadIds, setPendingReadIds] = useState<Set<string>>(new Set());
    const updateRecord = useUpdateRecord({ queryInvalidatePrefix: "notifications" });
    const navigate = useNavigate();

    const handleMarkAsRead = (notificationId: string) => {
        setFadingIds((prev) => new Set(prev).add(notificationId));
        updateRecord.mutate({
            id: notificationId,
            entity: "notifications",
            data: { is_read: true },
        });

        setTimeout(() => {
            setPendingReadIds((prev) => new Set(prev).add(notificationId));
            setFadingIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(notificationId);
                return newSet;
            });
        }, 300);
    };

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        notifications.setNotificationsList({ isOpen: false });
    };

    useEffect(() => {
        if (!badgeData) return;

        const visibleNotifications = badgeData?.items.filter((item: any) => {
            return item.item[0]?.item && !pendingReadIds.has(item.id) && !fadingIds.has(item.id);
        });
        setNotificationsNumber(visibleNotifications?.length);
    }, [badgeData, pendingReadIds, fadingIds]);

    return (
        <Box>
            <IconButton
                size="large"
                color="inherit"
                onClick={() => {
                    notifications.setNotificationsList({ isOpen: true });
                }}
            >
                <Notifications />
                <Badge badgeContent={notificationsNumber} color="error" />
            </IconButton>

            <MuiSnackBar
                open={notifications.notificationsList.isOpen}
                onClose={handleClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Box sx={{ width: "500px", bgcolor: "secondary.main", borderRadius: 2, boxShadow: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 5 }}>
                        <Typography>{notificationsNumber === 0 ? "Não tem novas notificações" : "Notificações"}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <MarkAllNotificationsRead
                                notifications={badgeData?.items || []}
                                notificationsCount={notificationsNumber || 0}
                            />
                            <CancelOutlined
                                sx={{ cursor: "pointer" }}
                                onClick={() => notifications.setNotificationsList({ isOpen: false })}
                            />
                        </Box>
                    </Box>
                    <List
                        sx={{
                            maxHeight: { xs: "600px", xxl: "800px" },
                            overflowY: "auto",
                            pl: 5,
                            pr: 5,
                            "&::-webkit-scrollbar": {
                                width: "8px",
                            },
                            "&::-webkit-scrollbar-track": {
                                background: "rgba(0, 0, 0, 0.05)",
                                borderRadius: "4px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                background: "rgba(0, 0, 0, 0.2)",
                                borderRadius: "4px",
                                "&:hover": {
                                    background: "rgba(0, 0, 0, 0.3)",
                                },
                            },
                        }}
                    >
                        {data?.items.map((notificationItem: NotificationItem) => {
                            if (notificationItem.is_read || pendingReadIds.has(notificationItem.id)) return null;

                            const relateditem = notificationItem.item[0];
                            if (!relateditem?.item?.date_created) return null;

                            const isFading = fadingIds.has(notificationItem.id);

                            return (
                                <ListItem
                                    key={notificationItem.id}
                                    disablePadding
                                    sx={{
                                        pb: 2,
                                        pt: 2,
                                        opacity: isFading ? 0 : 1,
                                        transition: "opacity 0.3s ease-out",
                                    }}
                                >
                                    <Card sx={{ width: "100%" }}>
                                        <CardContent>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
                                                <Avatar value={notificationItem.user_that_generated_notification?.avatar} />
                                                <ListItemText
                                                    primary={`${notificationItem.user_that_generated_notification?.first_name} ${notificationItem.user_that_generated_notification?.last_name}`}
                                                    secondary={`Criou um(a) ${getNotificationTypeLabel(
                                                        notificationItem.type
                                                    )} ${handleNotificationTinming(relateditem.item.date_created)}`}
                                                />
                                                {(notificationItem.type === "tasks" || notificationItem.type === "task_comments") && (
                                                    <Chip
                                                        label={getTaskStatus(notificationItem) === "open" ? "Aberta" : "Fechada"}
                                                        color={getTaskStatus(notificationItem) === "open" ? "warning" : "primary"}
                                                        size="small"
                                                    />
                                                )}
                                                <MarkEmailRead
                                                    sx={{ cursor: "pointer" }}
                                                    onClick={() => handleMarkAsRead(notificationItem.id)}
                                                    color="primary"
                                                />
                                            </Box>
                                            <CardActionArea
                                                sx={{ border: "1px solid rgba(0, 0, 0, 0.12)", borderRadius: 2, p: 4, mt: 4 }}
                                                onClick={() => {
                                                    navigate(notificationItem.navigation_path);
                                                    notifications.setNotificationsList({ isOpen: false });
                                                }}
                                            >
                                                <Typography>{removeHtmlTagsAndEntities(notificationItem.content)}</Typography>
                                            </CardActionArea>
                                        </CardContent>
                                    </Card>
                                </ListItem>
                            );
                        })}
                    </List>
                </Box>
            </MuiSnackBar>
        </Box>
    );
};

type NotificationUser = UserFromAPI & {
    avatar: string;
}

type RelatedItemData = { content: string; id: string; date_created: string; task?: { id: string; status?: string }; status?: string };

type RelatedItem = { collection: string; item: RelatedItemData };

type NotificationItem = {
    id: string;
    date_created: string;
    is_read: boolean;
    item: Array<RelatedItem>;
    user_that_generated_notification: NotificationUser;
    user_to_be_notified: NotificationUser;
    content: string;
    navigation_path: string;
    type: string;
};

const handleNotificationTinming = (date_created: string) => {
    return formatDistanceToNow(parseISO(date_created), {
        addSuffix: true,
        locale: pt,
    });
};

const getTaskStatus = (notification: NotificationItem) => {
    const relatedItem = notification.item[0]?.item;
    if (notification.type === "tasks") return relatedItem?.status;
    if (notification.type === "task_comments") return relatedItem?.task?.status;
    return null;
};
